<script>
import { defineComponent, ref, watch, computed, inject } from 'vue'
import { useQuestionKeyboardNav } from '@/composables/useQuestionKeyboardNav'
import { buildDraftInputs, selectEvidence, draftEvidenceStatus, draftDisabledReason, draftGateHint } from '@rm-ui/levelling/rationaleDraft'
import RationaleEditor from './RationaleEditor.vue'

export default defineComponent({
  name: 'SubFactorQuestion',
  components: { RationaleEditor },
  props: {
    subFactor: {
      type: Object,
      required: true
    },
    factor: {
      type: Object,
      required: true
    },
    currentSubFactorIndex: {
      type: Number,
      required: true
    },
    totalSubFactors: {
      type: Number,
      required: true
    },
    previousAnswer: {
      type: Object,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: false
    },
    editing: {
      type: Boolean,
      default: false
    },
    steps: {
      type: Array,
      default: () => []
    },
    /* AI rationale drafting (PD-1301): { enabled, chainId, topK, systemReference,
       getEvidence(), ensureEvidence() } for the CURRENT unit, supplied by the
       holder. Null / disabled hides "Use Atlas". */
    rationaleDraft: {
      type: Object,
      default: null
    }
  },
  emits: ['answer', 'back', 'restart', 'cancel', 'delete', 'jumpToFactor'],
  setup(props, { emit }) {
    const loginState = inject('loginState');
    const selectedLevel = ref(props.previousAnswer?.selectedLevel ?? null)
    const comments = ref(props.previousAnswer?.comments ?? null)
    const rationaleProvenance = ref(props.previousAnswer?.rationaleProvenance ?? null)
    const expandedDescriptions = ref({})
    const showFactorInfo = ref(false)
    const factorColor = computed(() => {
      const cur = props.steps.find(s => s.current)
      return cur ? cur.color : null
    })

    function toggleDescription(levelNumber) {
      expandedDescriptions.value = {
        ...expandedDescriptions.value,
        [levelNumber]: !expandedDescriptions.value[levelNumber]
      }
    }

    const hasDescriptions = computed(() => (props.subFactor.levels || []).some(l => l.description))
    // "Show all" is satisfied when every describable level is already open — either
    // it's the selected level (always shows its description) or it's been expanded.
    const allDescriptionsShown = computed(() => {
      const withDesc = (props.subFactor.levels || []).filter(l => l.description)
      return withDesc.length > 0 && withDesc.every(l => selectedLevel.value === l.number || expandedDescriptions.value[l.number])
    })
    function toggleAllDescriptions() {
      if (allDescriptionsShown.value) {
        expandedDescriptions.value = {}
      } else {
        const map = {}
        for (const l of (props.subFactor.levels || [])) {
          if (l.description) map[l.number] = true
        }
        expandedDescriptions.value = map
      }
    }
    const updatedBy = computed(() => props.previousAnswer?.updated_by ?? loginState.user.name)
    const updatedDate = computed(() => props.previousAnswer && props.previousAnswer.comments ? props.previousAnswer.updated_date : null)

    // An empty rationale is stored as null, so normalise before comparing —
    // otherwise opening and closing the editor would look like a change.
    function normaliseComment(value) {
      const text = (value ?? '').trim()
      return text === '' ? null : text
    }

    // True only when the level or the rationale differs from the stored answer.
    // Drives the Update button and lets Next/Previous skip a pointless save.
    const isDirty = computed(() =>
      selectedLevel.value !== (props.previousAnswer?.selectedLevel ?? null) ||
      normaliseComment(comments.value) !== normaliseComment(props.previousAnswer?.comments) ||
      (normaliseComment(comments.value) !== null &&
        (rationaleProvenance.value ?? null) !== (props.previousAnswer?.rationaleProvenance ?? null))
    )

    watch(() => props.subFactor.id, () => {
      expandedDescriptions.value = {}
      showFactorInfo.value = false
    }, { immediate: true })

    watch(() => props.previousAnswer, (newVal) => {
      selectedLevel.value = newVal?.selectedLevel ?? null
      comments.value = newVal?.comments ?? null
      rationaleProvenance.value = newVal?.rationaleProvenance ?? null
    })

    function selectLevel(level) {
      selectedLevel.value = level
    }

    function submitAnswer() {
      if (selectedLevel.value !== null) {
        emit('answer', {
          level: selectedLevel.value,
          comments: comments.value,
          rationaleProvenance: normaliseComment(comments.value) === null ? null : rationaleProvenance.value,
          dirty: isDirty.value
        })
      }
    }

    const { keyHint } = useQuestionKeyboardNav({
      options: () => (props.subFactor.levels || []).map(l => ({ key: Math.round(l.score), value: l.number })),
      onSelect: selectLevel,
      onNext: submitAnswer,
      onPrev: () => emit('back'),
      canNext: () => selectedLevel.value !== null,
      canNavigate: () => !props.editing,
      verb: 'score',
    })

    // --- Rationale (PD-1301) ---
    // The editor hands back text + provenance; both ride with the answer on Next/Update.
    function onRationaleUpdate({ text, provenance }) {
      comments.value = text || null
      rationaleProvenance.value = text ? provenance : null
    }

    const canDraft = computed(() => !!(props.rationaleDraft && props.rationaleDraft.enabled))
    // Reactive: getEvidence reads the assist composable's state, so this
    // re-evaluates as the analysis for this unit lands.
    const evidenceStatus = computed(() => draftEvidenceStatus(props.rationaleDraft, props.subFactor.name))
    const draftDisabled = computed(() => draftDisabledReason({
      levelSelected: selectedLevel.value !== null,
      evidenceStatus: evidenceStatus.value,
    }))
    const draftPending = computed(() => evidenceStatus.value === 'pending')
    const draftHint = computed(() => draftGateHint(evidenceStatus.value))
    // "Use Atlas" stays disabled while the analysis is pending, so make sure it
    // is actually running (the evaluator may have hidden the assist panel).
    watch(evidenceStatus, (s) => {
      if (s === 'pending' && props.rationaleDraft && props.rationaleDraft.ensureEvidence) props.rationaleDraft.ensureEvidence()
    }, { immediate: true })

    // Built at click time: the chain is grounded on the score chosen NOW and the
    // AI-assist evidence for this sub-factor, ranked and trimmed to topK.
    function buildDraftRequest() {
      const ctx = props.rationaleDraft || {}
      const level = (props.subFactor.levels || []).find(l => l.number === selectedLevel.value) || null
      return {
        chainId: ctx.chainId,
        systemReference: ctx.systemReference,
        sourceCustom: { factor_id: props.factor.id, sub_factor_id: props.subFactor.id, level: selectedLevel.value },
        getEvidence: ctx.getEvidence,
        ensureEvidence: ctx.ensureEvidence,
        buildInputs: (items) => buildDraftInputs({
          factor: props.factor,
          subFactor: props.subFactor,
          level,
          evidence: selectEvidence(items, { subFactorName: props.subFactor.name, topK: ctx.topK }),
        }),
      }
    }

    return {
      selectedLevel,
      selectLevel,
      submitAnswer,
      isDirty,
      comments,
      rationaleProvenance,
      updatedBy,
      updatedDate,
      onRationaleUpdate,
      canDraft,
      draftDisabled,
      draftPending,
      draftHint,
      buildDraftRequest,
      expandedDescriptions,
      toggleDescription,
      hasDescriptions,
      allDescriptionsShown,
      toggleAllDescriptions,
      showFactorInfo,
      factorColor,
      keyHint,
    }
  }
})
</script>

<template>
  <div class="lvl-eval">
    <div class="lvl-qcard">
      <div class="lvl-qcard-in">
        <div class="lvl-q-head">
          <!-- Coloured factor stepper (click a completed factor to go back to it) -->
          <div v-if="steps.length && !editing" class="lvl-fsegs" aria-label="Factor progress">
            <button
              v-for="(step, i) in steps"
              :key="i"
              type="button"
              class="lvl-fseg"
              :class="{ 'is-done': step.done, 'is-cur': step.current, 'is-locked': !step.clickable && !step.current }"
              :style="{ '--seg': step.color }"
              :disabled="!step.clickable"
              :aria-label="step.name"
              :aria-current="step.current ? 'step' : undefined"
              @click="step.clickable && $emit('jumpToFactor', i)"
            >
              <span class="lvl-fseg-tip" aria-hidden="true">
                <span class="lvl-fseg-tip-head"><strong>{{ step.name }}</strong></span>
                <span class="lvl-fseg-tip-state" :class="{ 'is-locked': !step.clickable && !step.current }">
                  <span class="ms" style="font-size:13px">{{ step.done ? 'check_circle' : step.current ? 'radio_button_checked' : 'lock' }}</span>
                  {{ step.done ? 'Answered — select to revisit' : step.current ? 'You’re here' : 'Answer the earlier factors to reach this' }}
                </span>
              </span>
            </button>
          </div>

          <!-- Parent factor name -->
          <div class="lvl-q-eyebrow">
            <span v-if="factorColor" class="lvl-fdot" :style="{ background: factorColor }"></span>
            <div class="lvl-ghead-title">{{ factor.name }}</div>
            <span v-if="factor.description" class="lvl-ghead-info">
              <button
                type="button"
                class="lvl-ghead-ibtn"
                :class="{ on: showFactorInfo }"
                :aria-expanded="showFactorInfo"
                :aria-label="'About ' + factor.name"
                title="What this factor covers"
                @click="showFactorInfo = !showFactorInfo"
              >
                <span class="ms" style="font-size:15px">info</span>
              </button>
              <template v-if="showFactorInfo">
                <div class="lvl-help-catch" @click="showFactorInfo = false"></div>
                <div class="lvl-ghead-pop" role="tooltip">
                  <span class="lvl-ghead-pop-title">{{ factor.name }}</span>
                  <p>{{ factor.description }}</p>
                </div>
              </template>
            </span>
          </div>

          <hr class="lvl-q-divider" />
        </div>

        <div class="lvl-q-body">

          <!-- Sub-factor title -->
          <h2 class="lvl-q-title">{{ subFactor.name }}</h2>

          <!-- Description -->
          <p v-if="subFactor.description" class="lvl-q-desc">{{ subFactor.description }}</p>

          <!-- Level selection prompt -->
          <div class="lvl-q-prompt">
            Choose the knowledge level for this role:
            <span class="lvl-q-scorehint">higher means greater demand</span>
            <button
              v-if="hasDescriptions"
              type="button"
              class="lvl-q-showall"
              :aria-pressed="allDescriptionsShown"
              @click="toggleAllDescriptions"
            >
              <span class="ms" style="font-size:14px">{{ allDescriptionsShown ? 'unfold_less' : 'unfold_more' }}</span>
              {{ allDescriptionsShown ? 'Collapse all' : 'Show all' }}
            </button>
          </div>

          <!-- Scored level options -->
          <div class="lvl-q-opts lvl-q-opts--scored" role="radiogroup">
          <div
            v-for="level in subFactor.levels"
            :key="level.number"
            class="lvl-scoreopt"
            :class="{
              sel: selectedLevel === level.number,
              'is-open': selectedLevel === level.number || expandedDescriptions[level.number]
            }"
          >
            <button
              type="button"
              role="radio"
              :aria-checked="selectedLevel === level.number"
              class="lvl-scoreopt-hit"
              @click="selectLevel(level.number)"
              :data-testid="'subfactor-' + subFactor.id + '-option-' + Math.round(level.score)"
            >
              <span class="lvl-scoreopt-n">{{ Math.round(level.score) }}</span>
              <span class="lvl-scoreopt-body">
                <span class="lvl-scoreopt-name">{{ level.name }}</span>
                <span
                  v-if="level.description && (selectedLevel === level.number || expandedDescriptions[level.number])"
                  class="lvl-scoreopt-d"
                >{{ level.description }}</span>
              </span>
            </button>
            <button
              v-if="level.description"
              type="button"
              class="lvl-scoreopt-chev"
              :aria-label="expandedDescriptions[level.number] ? 'Hide description' : 'Show description'"
              @click="toggleDescription(level.number)"
            >
              <span class="ms">expand_more</span>
            </button>
          </div>
        </div>

        </div>

        <hr class="lvl-q-divider" />

        <!-- Rationale (PD-1301: with "Use Atlas") -->
        <RationaleEditor
          :comment="comments"
          :provenance="rationaleProvenance"
          :author="updatedBy"
          :date="updatedDate"
          :read-only="readOnly"
          :can-draft="canDraft"
          :draft-disabled-reason="draftDisabled"
          :draft-pending="draftPending"
          :draft-hint="draftHint"
          :draft-request="buildDraftRequest"
          @update="onRationaleUpdate"
        />
      </div>

      <!-- Editing a single answer from the summary: only Cancel / Update -->
      <div v-if="editing" class="lvl-cardfoot">
        <span class="restart"></span>
        <span class="prog">Editing one answer · your other answers stay as they are</span>
        <span class="nav">
          <button type="button" class="lvl-btn lvl-btn--text" @click="$emit('cancel')">Cancel</button>
          <button type="button" class="lvl-btn" :disabled="selectedLevel === null || !isDirty" @click="submitAnswer"><span class="ms">check</span> Update</button>
        </span>
      </div>
      <div v-else class="lvl-cardfoot">
        <span class="restart">
          <button type="button" class="lvl-btn lvl-btn--outlined" :disabled="readOnly" @click="$emit('restart')">Restart</button>
          <button v-if="canDelete" type="button" class="lvl-btn lvl-btn--danger" :disabled="readOnly" @click="$emit('delete')">Delete</button>
        </span>
        <span class="prog">factor questions {{ currentSubFactorIndex + 1 }} of {{ totalSubFactors }}<template v-if="keyHint"> · {{ keyHint }}</template></span>
        <span class="nav">
          <button type="button" class="lvl-btn lvl-btn--outlined" @click="$emit('back')">Previous</button>
          <button type="button" class="lvl-btn" :disabled="selectedLevel === null" @click="submitAnswer">Next <span class="ms">arrow_forward</span></button>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./evaluationTheme.scss"></style>

<style scoped>
.rationale-meta {
  margin-top: 8px;
  font-size: 11px;
  color: var(--v3-text-3);
}
.lvl-q-eyebrow {
  display: flex;
  align-items: center;
  margin: 0;
}
.lvl-ghead-title {
  display: inline-flex;
  align-items: center;
  line-height: 1;
  font-size: 12px;
  margin-top: 2px;
}
.lvl-fdot {
  display: inline-block;
  width: 22px;
  height: 9px;
  border-radius: 999px;
  flex: 0 0 auto;
}
.eval-docked .lvl-qcard { max-height: calc(100dvh - 158px); }
.eval-docked .lvl-qcard-in { flex: 1 1 auto; min-height: 0; }
.eval-docked .lvl-q-head { flex: 0 0 auto; }
.eval-docked .lvl-q-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
  padding-right: 10px;
  padding-bottom: 5px;
}

</style>
